import { useEffect, useState } from 'react';
import { ArrowUpRight, Building2, Check, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { PLANS } from '../../plans';
import { Alert, Badge, Button, FormField, PageHeader, PlanBadge, SectionHeader, Skeleton, UsageMeter } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import { generationsThisMonth, setupSteps } from './insights';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const GUIDES = {
  gbp_connected: 'How%20to%20Add%20GBP%20Access',
  facebook_connected: 'How%20to%20Add%20FB%20Access',
  media_uploaded: 'How%20to%20Upload%20Media',
  tracking_ready: 'How%20to%20Configure%20Rank%20Tracking',
};

export default function SetupPage() {
  const { user } = useAuth();
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();
  const stored = workspace.optimization;

  const [form, setForm] = useState({ business: '', website: '', location: '', gbpConnected: false, facebookConnected: false, mediaUploaded: false, trackingReady: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!stored) return;
    setForm({
      business: stored.business || '', website: stored.website || '', location: stored.location || '',
      gbpConnected: Boolean(stored.gbp_connected), facebookConnected: Boolean(stored.facebook_connected),
      mediaUploaded: Boolean(stored.media_uploaded), trackingReady: Boolean(stored.tracking_ready),
    });
  }, [stored]);

  const save = async (next = form) => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/workspace/optimization`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(next) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save your setup.');
      await reload();
      notify('Setup saved.');
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={140} /><Skeleton height={280} /></div>;

  const steps = setupSteps(stored);
  const done = steps.filter(step => step.done).length;
  const percent = Math.round((done / steps.length) * 100);
  const plan = workspace.plan || 'launch';
  const used = generationsThisMonth(workspace.generations);

  const toggle = key => {
    const next = { ...form, [key]: !form[key] };
    setForm(next);
    save(next);
  };

  const FIELD_KEY = { gbp_connected: 'gbpConnected', facebook_connected: 'facebookConnected', media_uploaded: 'mediaUploaded', tracking_ready: 'trackingReady' };

  return (
    <div className="dt-stack-5">
      <PageHeader title="Setup" description="Connect your accounts so tracking, reviews, and recommendations stay accurate." />

      <section className="dt-card setup-progress" aria-label="Setup progress">
        <SectionHeader
          title={`Business setup · ${percent}% complete`}
          description={done === steps.length ? 'Every connection is complete.' : `Complete ${steps.find(step => !step.done).label} to improve your recommendations.`}
        />
        <UsageMeter label="Steps complete" used={done} limit={steps.length} />
      </section>

      <div className="setup-layout">
        <div className="dt-stack-4">
          <section className="dt-card dt-stack-4" aria-label="Business profile">
            <SectionHeader title="Business profile" description="Used across scans, drafts, and reports." />
            <form className="dt-stack-4" onSubmit={event => { event.preventDefault(); save(); }}>
              <FormField label="Business name" id="setup-business"><input id="setup-business" className="dt-input" value={form.business} onChange={event => setForm(current => ({ ...current, business: event.target.value }))} placeholder="Acme Home Services" /></FormField>
              <div className="dt-field-grid">
                <FormField label="Primary website" id="setup-website"><input id="setup-website" className="dt-input" value={form.website} onChange={event => setForm(current => ({ ...current, website: event.target.value }))} placeholder="example.com" /></FormField>
                <FormField label="Primary market" id="setup-location"><input id="setup-location" className="dt-input" value={form.location} onChange={event => setForm(current => ({ ...current, location: event.target.value }))} placeholder="Orlando, FL" /></FormField>
              </div>
              {error && <Alert tone="danger">{error}</Alert>}
              <div><Button type="submit" loading={busy}>Save business profile</Button></div>
            </form>
          </section>

          <section className="dt-stack-3" aria-label="Connections">
            <SectionHeader title="Connections" description="Mark each step complete once it is set up. Support can walk you through any of them." />
            <ul className="setup-list">
              {steps.map(step => (
                <li className="setup-item" key={step.key}>
                  <input
                    type="checkbox"
                    id={`step-${step.key}`}
                    checked={Boolean(form[FIELD_KEY[step.key]])}
                    onChange={() => toggle(FIELD_KEY[step.key])}
                  />
                  <span>
                    <label htmlFor={`step-${step.key}`}><strong>{step.label}</strong></label>
                    <small>{step.done ? 'Complete' : 'Not connected yet'}</small>
                  </span>
                  {step.done
                    ? <Badge tone="success"><Check size={12} /> Done</Badge>
                    : <Button variant="secondary" size="sm" href={`mailto:host@loadedloaded.com?subject=${GUIDES[step.key]}`}>Get help <ExternalLink size={13} /></Button>}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="dt-stack-4">
          <section className="dt-card dt-stack-4" aria-label="Plan and usage">
            <SectionHeader title="Plan & usage" />
            <div className="shell-plan-head"><span className="dt-label">Current plan</span><PlanBadge plan={plan} /></div>
            <p className="dt-support">{PLANS[plan]?.tagline}</p>
            <UsageMeter label="AI generations this month" used={used} limit={workspace.limits?.aiGenerations || 25} />
            <UsageMeter label="Leads stored" used={(workspace.prospects || []).length} limit={workspace.limits?.leads || 100} />
            {plan !== 'revenue_partner' && <Button to="/#pricing">Upgrade plan <ArrowUpRight size={14} /></Button>}
          </section>

          <section className="dt-card dt-stack-3" aria-label="Account">
            <SectionHeader title="Account" />
            <dl className="lead-detail-list">
              <div><dt>Name</dt><dd>{user?.name}</dd></div>
              <div><dt>Email</dt><dd>{user?.email}</dd></div>
              <div><dt>Account ID</dt><dd>usr_{user?.id}</dd></div>
              <div><dt>Role</dt><dd style={{ textTransform: 'capitalize' }}>{user?.role}</dd></div>
            </dl>
            <Button variant="secondary" href="mailto:host@loadedloaded.com?subject=Data%20Tallied%20account"><Building2 size={14} />Contact support</Button>
          </section>
        </div>
      </div>
    </div>
  );
}
